import { useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Menu,
  Key,
  LogOut,
  ChevronDown,
  User,
  Building,
  PlusCircle
} from "lucide-react";
import { GlobalContext } from "../../../app/providers/GlobalProvider";
import { ROUTES, STORAGE_KEYS } from "../../utils/constants";
import { toast } from "react-toastify";
import { NotificationBell, NotificationPanel } from "../notifications";
import { ImagoModal, ImagoModalBody, ImagoModalFooter } from "../ui/ImagoModal";
import { ImagoButton, ImagoIconButton } from "../ui/ImagoButton";
import { ImagoThemeToggle } from "../ui/ImagoThemeToggle";
import { ImagoInput } from "../ui/ImagoInput";
import { clearOrchestrationCache } from "../../../store/slices/orchestrationSlice";
import { useSandboxMode } from "../../../features/auth/hooks/useSandboxMode";
import AgentStatusIndicator from "../agent/AgentStatusIndicator";
import AgentTokenDialog from "../agent/AgentTokenDialog";
import RoleSelector from "../../../features/roles/components/RoleSelector";
import ScheduledRunEmailToggle from "../../../features/notifications/components/ScheduledRunEmailToggle";
import { clearToken } from '../../auth/session';
import { readToken } from '../../auth/session';

const Navbar = ({ onMobileMenuClick }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNotifications, setShowNotifications] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isAgentDialogOpen, setIsAgentDialogOpen] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { appSidebarToggleRef } = useContext(GlobalContext);
  const userName = sessionStorage.getItem(STORAGE_KEYS.USERNAME);
  const userRole = sessionStorage.getItem(STORAGE_KEYS.ROLE);
  const organizationName = sessionStorage.getItem(STORAGE_KEYS.ORGANIZATION_NAME);
  const organizationRole = sessionStorage.getItem(STORAGE_KEYS.ORGANIZATION_ROLE);
  const isSandbox = useSandboxMode();
  const dropdownRef = useRef(null);

  const handleLogout = () => {
    setShowDropdown(false);

    // Clear Redux orchestration state (this also clears sessionStorage for orchestrations)
    dispatch(clearOrchestrationCache());

    // Clear remaining session storage items
    clearToken();
    sessionStorage.removeItem(STORAGE_KEYS.PROJECTS);
    sessionStorage.removeItem("listOrchestrations");
    localStorage.removeItem("activeEnvironment");
    sessionStorage.removeItem(STORAGE_KEYS.USERNAME);
    sessionStorage.removeItem(STORAGE_KEYS.ROLE);
    sessionStorage.removeItem(STORAGE_KEYS.ORCHESTRATIONS);
    sessionStorage.removeItem(STORAGE_KEYS.ORGANIZATION_ID);
    sessionStorage.removeItem(STORAGE_KEYS.ORGANIZATION_NAME);
    sessionStorage.removeItem(STORAGE_KEYS.ORGANIZATION_ROLE);
    sessionStorage.removeItem(STORAGE_KEYS.INVITE_STATUS);
    sessionStorage.removeItem(STORAGE_KEYS.USERID);
    sessionStorage.removeItem(STORAGE_KEYS.ACTIVE_PARTICIPANT_ROLE);
    navigate(ROUTES.SIGN_IN);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getInitials = (name) => {
    if (!name) return "";
    // Filter out empty strings that could result from multiple/trailing spaces
    const nameParts = name.split(" ").filter(part => part.length > 0);
    if (nameParts.length === 0) return "";
    const initials =
      nameParts.length > 1
        ? (nameParts[0][0] || "") + (nameParts[1][0] || "")
        : nameParts[0][0] || "";
    return initials.toUpperCase();
  };

  const handlePasswordChange = async () => {
    try {
      const token = readToken();

      if (!token) {
        toast.error("Authorization token is missing. Please log in again.");
        return;
      }

      const trimmedNewPassword = newPassword.trim();
      const trimmedConfirmPassword = confirmPassword.trim();

      if (trimmedNewPassword !== trimmedConfirmPassword) {
        toast.error("New and confirm password do not match.");
        return;
      }

      setIsChangingPassword(true);

      const response = await fetch("/api/users/changePassword", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          payload: { oldPassword, newPassword: trimmedNewPassword },
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success("Password changed successfully!");
        setIsChangePasswordModalOpen(false);
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        toast.error(data.message || "Failed to change password.");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <>
      <nav className="h-header px-4 bg-background border-b border-border flex items-center justify-between">
        {/* Left section */}
        <div className="flex items-center gap-3">
          {/* Mobile hamburger menu */}
          <ImagoIconButton
            size="lg"
            label="Toggle navigation menu"
            className="md:hidden"
            onClick={() => {
              if (onMobileMenuClick) onMobileMenuClick();
              else appSidebarToggleRef?.current?.();
            }}
          >
            <Menu className="w-5 h-5" />
          </ImagoIconButton>

          {/* Logo */}
          <div className="flex items-center" data-walkthrough="logo">
            <img
              src="/logo.png"
              alt="Imago Logo"
              className="h-8 w-auto"
            />
          </div>
        </div>

        {/* Right section */}
        <div className="flex items-center gap-2">
          {/* Agent Status — greyed out for sandbox users */}
          <div className={isSandbox ? 'opacity-40 pointer-events-none' : ''} title={isSandbox ? 'Upgrade to access' : undefined}>
            <AgentStatusIndicator onClick={() => setIsAgentDialogOpen(true)} />
          </div>

          {/* Role Selector */}
          <RoleSelector />

          {/* Theme Toggle */}
          <ImagoThemeToggle size="lg" data-walkthrough="theme-toggle" />

          {/* Notification Bell */}
          <div data-walkthrough="notifications">
            <NotificationBell onClick={() => setShowNotifications(!showNotifications)} />
          </div>

          {/* User profile section */}
          <div className="flex items-center gap-2" data-walkthrough="user-profile">
            {/* Avatar */}
            <div className="w-8 h-8 bg-card border border-border rounded-full flex items-center justify-center">
              {userName ? (
                <span className="text-xs font-medium text-foreground">{getInitials(userName)}</span>
              ) : (
                <User className="w-4 h-4 text-muted" />
              )}
            </div>

            {/* User info (desktop only) */}
            <div className="hidden md:flex flex-col min-w-0 max-w-[150px]">
              <span className="text-sm font-medium text-foreground truncate" title={userName}>
                {userName}
              </span>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-xs text-muted truncate">
                  {userRole}
                </span>
                {organizationName && (
                  <>
                    <span className="text-xs text-muted/30">•</span>
                    <span
                      className="px-1.5 py-0.5 text-label font-semibold bg-primary/10 text-blue-700 dark:text-blue-300 rounded-md border border-primary/20 truncate"
                      title={organizationName}
                    >
                      {organizationName}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Dropdown trigger */}
            <div ref={dropdownRef} className="relative">
              <ImagoIconButton
                size="md"
                label="User menu"
                active={showDropdown}
                onClick={() => setShowDropdown((prev) => !prev)}
              >
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-base ease-standard ${showDropdown ? 'rotate-180' : ''}`}
                />
              </ImagoIconButton>

              {/* Dropdown menu */}
              {showDropdown && (
                <div className="
                  absolute right-0 mt-1 w-72 bg-dropdown border border-border rounded-lg
                  shadow-theme-lg z-dropdown py-1 overflow-hidden
                ">
                  <button
                    onClick={() => {
                      setShowDropdown(false);
                      setIsChangePasswordModalOpen(true);
                    }}
                    className="
                      flex items-center gap-3 w-full px-4 py-2 text-sm text-left text-foreground
                      hover:bg-dropdown-hover transition-colors duration-fast ease-standard
                    "
                  >
                    <Key className="w-4 h-4 text-muted" />
                    Change Password
                  </button>

                  {(organizationRole === 'SUPER_ADMIN' || organizationRole === 'ADMIN') && (
                    <button
                      onClick={() => {
                        setShowDropdown(false);
                        navigate(ROUTES.ORGANIZATION_ADMIN);
                      }}
                      className="
                        flex items-center gap-3 w-full px-4 py-2 text-sm text-left text-foreground
                        hover:bg-dropdown-hover transition-colors duration-fast ease-standard
                      "
                    >
                      <Building className="w-4 h-4 text-muted" />
                      Organization Admin
                    </button>
                  )}

                  {isSandbox && (
                    <button
                      onClick={() => {
                        setShowDropdown(false);
                        navigate(ROUTES.ORGANIZATION_SIGNUP);
                      }}
                      className="
                        flex items-center gap-3 w-full px-4 py-2 text-sm text-left text-foreground
                        hover:bg-dropdown-hover transition-colors duration-fast ease-standard
                      "
                    >
                      <PlusCircle className="w-4 h-4 text-muted" />
                      Create Organization
                    </button>
                  )}

                  <ScheduledRunEmailToggle />

                  <div className="border-t border-border my-1" />

                  <button
                    onClick={handleLogout}
                    className="
                      flex items-center gap-3 w-full px-4 py-2.5 text-sm text-danger
                      hover:bg-danger/10 transition-colors
                    "
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Notification Panel */}
      <NotificationPanel
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
      />

      {/* Agent Token Dialog */}
      <AgentTokenDialog
        isOpen={isAgentDialogOpen}
        onClose={() => setIsAgentDialogOpen(false)}
      />

      {/* Change Password Modal */}
      <ImagoModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => {
          if (!isChangingPassword) {
            setIsChangePasswordModalOpen(false);
            setOldPassword("");
            setNewPassword("");
            setConfirmPassword("");
          }
        }}
        title="Change Password"
        size="md"
      >
        <ImagoModalBody>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Old Password <span className="text-danger">*</span>
              </label>
              <ImagoInput
                type="password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                placeholder="Enter old password"
                disabled={isChangingPassword}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                New Password <span className="text-danger">*</span>
              </label>
              <ImagoInput
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                disabled={isChangingPassword}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Confirm Password <span className="text-danger">*</span>
              </label>
              <ImagoInput
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                disabled={isChangingPassword}
              />
            </div>
          </div>
        </ImagoModalBody>

        <ImagoModalFooter>
          <ImagoButton
            variant="secondary"
            onClick={() => {
              setIsChangePasswordModalOpen(false);
              setOldPassword("");
              setNewPassword("");
              setConfirmPassword("");
            }}
            disabled={isChangingPassword}
          >
            Cancel
          </ImagoButton>
          <ImagoButton
            variant="primary"
            onClick={handlePasswordChange}
            loading={isChangingPassword}
            disabled={!oldPassword || !newPassword || !confirmPassword}
          >
            Update Password
          </ImagoButton>
        </ImagoModalFooter>
      </ImagoModal>
    </>
  );
};

export default Navbar;
